import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Edit3, CheckCircle, Clock, AlertTriangle, Share2, Scale } from 'lucide-react';

interface ReportHeaderProps {
  report: {
    id: string;
    client_name: string;
    claimant_name: string;
    business_address: string;
    status: string;
  };
  onShareClick: () => void;
}

export const ReportHeader: React.FC<ReportHeaderProps> = ({ report, onShareClick }) => {
  return (
    <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{report.client_name}</h1>
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
            style={
              report.status === 'completed' ? { backgroundColor: 'transparent', color: 'var(--color-verified)' } :
              report.status === 'causation_failed' ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } :
              report.status === 'processing' ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } :
              report.status === 'failed' ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } :
              { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }
            }
          >
            {report.status === 'completed' && <CheckCircle className="w-3 h-3" />}
            {report.status === 'processing' && <Clock className="w-3 h-3" />}
            {report.status === 'causation_failed' && <AlertTriangle className="w-3 h-3" />}
            {report.status === 'causation_failed' ? 'Needs Attention' : report.status}
          </span>
        </div>
        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{report.claimant_name} • {report.business_address}</p>
      </div>
      
      <div className="flex items-center gap-2">
        {/* Adjuster Analysis Button - Show for completed reports */}
        {report.status === 'completed' && (
          <Link
            to={`/adjuster/${report.id}`}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition shadow-lg"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
            data-testid="adjuster-analysis-btn"
          >
            <Scale className="w-4 h-4" />
            Adjuster Analysis
          </Link>
        )}
        {(report.status === 'draft' || report.status === 'causation_failed' || report.status === 'processing' || report.status === 'failed') && (
          <Link
            to={`/wizard-v2?resume=${report.id}`}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition shadow-lg"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            <Edit3 className="w-4 h-4" />
            {report.status === 'draft' ? 'Resume' : 
             report.status === 'causation_failed' ? 'Edit & Fix' :
             report.status === 'processing' ? 'View Progress' :
             'Retry'}
          </Link>
        )}
        <button
          onClick={onShareClick}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition"
          style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
        >
          <Share2 className="w-4 h-4" />
          Share
        </button>
      </div>
    </div>
  );
};

export default ReportHeader;
